import React, { useState, useEffect } from 'react';
import { Product, ProductFilters as Filters, Category } from '@/domain/entities';
import { productService } from '@/application/services';
import { categoryService } from '@/application/services';
import { ProductFilters } from '../components/ProductFilters';
import { ProductTable } from '../components/ProductTable';
import { ProductForm } from '../components/ProductForm';
import { ProductPreview } from '../components/ProductPreview';
import { ImportModal } from '../components/ImportModal';
import { Pagination } from '../components/Pagination';
import { HttpError } from '@/domain/entities';
import { ProductSize } from '@/domain/entities';

export const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    limit: 20,
    offset: 0,
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [previewProduct, setPreviewProduct] = useState<Product | null>(null);
  const [previewSizes, setPreviewSizes] = useState<ProductSize[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [filters]);

  const loadCategories = async () => {
    try {
      const data = await categoryService.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories', err);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await productService.getProducts(filters);
      setProducts(response.products || []);
      setTotal(response.total || 0);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else {
        setError('Не удалось загрузить товары');
      }
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sku: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот товар?')) return;
    
    try {
      await productService.deleteProduct(sku);
      loadProducts();
    } catch (err) {
      if (err instanceof HttpError) {
        alert(err.data?.error || err.message);
      } else {
        alert('Не удалось удалить товар');
      }
    }
  };

  const handlePageChange = (newOffset: number) => {
    setFilters((prev) => ({ ...prev, offset: newOffset }));
  };

  const handleFiltersChange = (newFilters: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters, offset: 0 }));
  };

  const handleFormClose = () => {
    setShowCreateForm(false);
    setEditingProduct(null);
    loadProducts();
  };

  const handleProductExists = async (sku: string) => {
    // Товар уже существует - загружаем его данные и переключаем в режим редактирования
    try {
      const response = await productService.getProduct(sku);
      setEditingProduct(response.product);
      setShowCreateForm(false); // Закрываем форму создания
    } catch (err) {
      console.error('Failed to load existing product', err);
      if (err instanceof HttpError) {
        alert(err.data?.error || err.message);
      } else {
        alert('Не удалось загрузить товар для редактирования');
      }
    }
  };

  const handlePreview = async (product: Product) => {
    setPreviewProduct(product);
    try {
      const response = await productService.getProduct(product.sku);
      setPreviewSizes(response.sizes);
    } catch (err) {
      console.error('Failed to load product details', err);
      setPreviewSizes([]);
    }
  };

  const handlePreviewUpdate = () => {
    loadProducts();
  };

  const handlePreviewClose = () => {
    setPreviewProduct(null);
    setPreviewSizes([]);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Товары</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Управление товарами магазина
              {!loading && total > 0 && (
                <span className="ml-2 text-gray-700 dark:text-gray-300 font-medium">
                  • Найдено: {total}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowImportModal(true)}
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Импорт Excel
            </button>
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              Добавить товар
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex-shrink-0 mt-4">
        <ProductFilters
          filters={filters}
          onChange={handleFiltersChange}
          onReset={() => setFilters({ limit: 20, offset: 0 })}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex-shrink-0 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-2 rounded">
          {error}
        </div>
      )}

      {/* Table with Pagination */}
      <div className="flex-1 flex flex-col min-h-0 mt-4">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">Загрузка...</div>
        ) : (
          <>
            <div className="flex-1 overflow-auto min-h-0">
              <ProductTable
                products={products}
                categories={categories}
                onEdit={setEditingProduct}
                onDelete={handleDelete}
                onPreview={handlePreview}
              />
            </div>
            <div className="flex-shrink-0 mt-4">
              <Pagination
                total={total}
                limit={filters.limit || 20}
                offset={filters.offset || 0}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>

      {(showCreateForm || editingProduct) && (
        <ProductForm
          product={editingProduct || undefined}
          onClose={handleFormClose}
          onProductExists={handleProductExists}
        />
      )}

      {previewProduct && (
        <ProductPreview
          product={previewProduct}
          sizes={previewSizes}
          onClose={handlePreviewClose}
          onUpdate={handlePreviewUpdate}
        />
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            loadProducts();
          }}
        />
      )}
    </div>
  );
};

